"use client";

import * as Dialog from "@radix-ui/react-dialog";
import Image from "next/image";
import { X, ArrowLeft, ArrowRight, Images } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { resolveMediaUrl } from "@/lib/media";
import { cn } from "@/lib/format";
import type { Media } from "@/lib/dto";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "@/components/ui/Carousel";

type Props = { items: Media[]; title?: string };

type Img = { src: string; alt: string; caption: string | null };

/** Slides whose image is in the initial HTML: the first (LCP) and the next,
 *  which peeks in on lg+ (hidden below lg until warm, so phones don't fetch
 *  it — a display:none lazy image isn't loaded). The rest mount after load
 *  or on interaction. */
const EAGER_SLIDES = 2;
const WARM_AFTER_LOAD_MS = 2000;

function GalleryArrows() {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } =
    useCarousel();
  return (
    <div className="absolute inset-x-6 top-[calc(50%-20px)] z-10 flex flex-wrap items-center justify-between gap-x-1 text-center lg:inset-x-10">
      <button
        type="button"
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        aria-label="Previous photo"
        className="inline-flex h-8 w-8 items-center z-50 justify-center rounded-full border border-line bg-white text-primary shadow-md hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-10 md:w-10"
      >
        <svg
          width={16}
          height={16}
          viewBox="0 0 17 24"
          fill="currentColor"
          className="rotate-180"
        >
          <path d="M17 12L0 24L4.21852 12L0 0L17 12Z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={scrollNext}
        disabled={!canScrollNext}
        aria-label="Next photo"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line bg-white text-primary shadow-md hover:bg-primary hover:text-white disabled:pointer-events-none disabled:opacity-50 md:h-10 md:w-10"
      >
        <svg width={16} height={16} viewBox="0 0 17 24" fill="currentColor">
          <path d="M17 12L0 24L4.21852 12L0 0L17 12Z" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Hero gallery for the package detail page — matches production's real
 * markup exactly (confirmed via the live DOM): a single-row carousel
 * (75%-width main slide + a peek of the next one on lg+), overlaid
 * prev/next triangle arrow buttons, and a "View all (N)" button in the
 * bottom-right corner of the active slide that opens the full lightbox.
 * Replaces the earlier 4-tile bento mosaic, which didn't match production.
 */
export function TripGalleryMosaic({ items, title = "Gallery" }: Props) {
  const images: Img[] = (items ?? [])
    .map((m) => {
      const src = m?.url ? resolveMediaUrl(m.url) : null;
      return src
        ? { src, alt: m?.alt || "", caption: m?.caption ?? null }
        : null;
    })
    .filter((x): x is Img => x !== null);

  const [index, setIndex] = useState<number | null>(null);
  const close = useCallback(() => setIndex(null), []);

  // Off-screen slides used to start downloading alongside the LCP image and
  // compete with it for bandwidth on mobile. Mount them once the page has
  // loaded (plus a beat), or immediately when the visitor reaches for the
  // gallery (hover, touch, focus) — before they can swipe to them.
  const [warm, setWarm] = useState(false);
  const warmUp = useCallback(() => setWarm(true), []);
  useEffect(() => {
    if (warm) return;
    let timer: number | undefined;
    const schedule = () => {
      timer = window.setTimeout(warmUp, WARM_AFTER_LOAD_MS);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      window.removeEventListener("load", schedule);
      window.clearTimeout(timer);
    };
  }, [warm, warmUp]);
  const prev = useCallback(
    () =>
      setIndex((i) =>
        i == null ? null : (i - 1 + images.length) % images.length,
      ),
    [images.length],
  );
  const next = useCallback(
    () => setIndex((i) => (i == null ? null : (i + 1) % images.length)),
    [images.length],
  );

  useEffect(() => {
    if (index == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, prev, next, close]);

  if (!images.length) return null;

  return (
    <div className="container">
      <div
        className="relative overflow-hidden rounded-md"
        onPointerEnter={warmUp}
        onTouchStart={warmUp}
        onFocus={warmUp}
      >
        <Carousel opts={{ align: "start", loop: false }}>
          <GalleryArrows />
          <CarouselContent className="-ml-[5px]">
            {images.map((img, i) => (
              <CarouselItem
                key={img.src}
                className="basis-full pl-[5px] lg:basis-3/4"
              >
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`View photo ${i + 1}`}
                  className="group relative image-slot block aspect-[890/490] w-full overflow-hidden rounded-md bg-[color:var(--color-bg)]"
                >
                  {warm || i < EAGER_SLIDES ? (
                    <Image
                      src={img.src}
                      alt={img.alt}
                      fill
                      preload={i === 0}
                      fetchPriority={i === 0 ? "high" : undefined}
                      sizes="(min-width: 1024px) 75vw, 100vw"
                      className={cn(
                        "object-cover transition-transform duration-500 group-hover:scale-105",
                        i > 0 && !warm && "max-lg:hidden",
                      )}
                    />
                  ) : null}
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 2 ? (
            <button
              type="button"
              onClick={() => setIndex(0)}
              className="absolute right-6 bottom-6 z-10 inline-flex items-center rounded-full border border-white/10 bg-white/50 backdrop-blur-sm px-3 py-1 text-xs font-bold text-headings/80"
            >
              <Images
                size={16}
                aria-hidden
                className="mr-1 inline-block align-middle"
              />
              View all ({images.length})
            </button>
          ) : null}
        </Carousel>
      </div>

      <Dialog.Root
        open={index !== null}
        onOpenChange={(open) => !open && close()}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/90" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center outline-none"
          >
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="absolute z-50 top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <X size={20} aria-hidden />
            </Dialog.Close>
            {index !== null ? (
              <div className="relative flex h-full w-full items-center justify-center px-4">
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Previous"
                  className="absolute left-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ArrowLeft size={20} aria-hidden />
                </button>
                <div className="relative h-[80vh] w-full max-w-5xl">
                  <Image
                    src={images[index].src}
                    alt={images[index].alt}
                    fill
                    sizes="(min-width: 1024px) 1024px, 100vw"
                    className="object-contain"
                  />
                </div>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Next"
                  className="absolute right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ArrowRight size={20} aria-hidden />
                </button>
              </div>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
